import { Alpine } from '../../core/alpine-core.js';
import { apiFetch } from '../api.js';
import { CURRENT_SEASON } from '../../core/current-season.js';

Alpine.data('teamsView', () => ({

    id: null,
    team: null,
    roster: [],
    inactive: [],
    reserves: [],
    loans: [],
    standings: null,
    position: null,
    selectedSeason: CURRENT_SEASON,
    availableSeasons: [],

    fixtures: [],
    upcoming: [],
    results: [],

    loading: true,
    error: '',
    placeholderLogo: '/assets/placeholder-team.png',

    async init() {
        try {
            this.id = this._getIdFromUrl();
            if (!this.id) throw new Error('Invalid team id in URL');
            this.selectedSeason = CURRENT_SEASON;
            await this._loadAll();
        } catch (e) {
            console.error(e);
            this.error = e?.message || 'Failed to load team';
        } finally {
            this.loading = false;
        }
    },

    async _loadAll() {
        // Team
        const tRes = await apiFetch(`/teams/${this.id}?populate=true&season=${encodeURIComponent(this.selectedSeason)}`);
        if (!tRes.ok) throw new Error('Team not found');
        const data = await tRes.json();
        this.team = data.team;
        this.availableSeasons = Array.isArray(this.team?.availableSeasons) && this.team.availableSeasons.length
            ? this.team.availableSeasons
            : [CURRENT_SEASON];
        this.selectedSeason = this.team?.seasonUsed || this.selectedSeason || CURRENT_SEASON;

        // Roster
        const rRes = await apiFetch(`/teams/${this.id}/roster?season=${encodeURIComponent(this.selectedSeason)}`);
        if (rRes.ok) {
            const r = await rRes.json();
            this.roster = r.roster || [];
            this.inactive = r.inactive || [];
            this.reserves = r.reserves || [];
        }

        // Standings (optional; ignore if not available)
        // const lg = this.team?.league || '';
        const sz = this.team?.seasonUsed || this.selectedSeason || CURRENT_SEASON;
        if (sz) {
            const sRes = await apiFetch(`/standings?season=${encodeURIComponent(sz)}`);
            if (sRes.ok) {
                const s = await sRes.json();
                this.standings = s.items || s.standings || [];
                const idx = this._findStandingsIndex(this.standings, this.team);
                this.position = (idx >= 0) ? idx + 1 : null;
            }
        }

        // Fixtures (up to 20, oldest→newest)
        const fRes = await apiFetch(`/fixtures?teamId=${this.id}&season=${encodeURIComponent(sz)}&limit=20&sort=startAt:asc`);
        if (fRes.ok) {
            const d = await fRes.json();
            const items = d.items || [];
            const now = Date.now();
            this.upcoming = items.filter(x => new Date(x.startAt).getTime() >= now).slice(0, 5);
            this.results = items.filter(x => new Date(x.startAt).getTime() < now).slice(-5);
            this.fixtures = items;
        }
    },

    async changeSeason() {
        this.loading = true;
        this.error = '';
        try {
            await this._loadAll();
        } catch (e) {
            console.error(e);
            this.error = e?.message || 'Failed to load team season';
        } finally {
            this.loading = false;
        }
    },

    // ---------- UI helpers ----------
    teamStat(key) {
        const cs = this.team?.currentStats;
        if (cs) return cs[key] ?? 0;

        const season = this.team?.seasonUsed || this.selectedSeason || CURRENT_SEASON;
        const arr = Array.isArray(this.team?.stats) ? this.team.stats : [];
        const row = season ? arr.find(s => s?.season === season) : arr[0];
        return row?.[key] ?? 0;
    },
    teamStatsRows() {
        return Array.isArray(this.team?.stats) ? this.team.stats : [];
    },
    teamPlayerSummary() {
        return this.team?.playerSeasonSummary || { totals: {}, leaders: {}, players: [] };
    },
    playerTotals(key) {
        return this.teamPlayerSummary()?.totals?.[key] ?? 0;
    },
    seasonPlayers() {
        return Array.isArray(this.teamPlayerSummary()?.players) ? this.teamPlayerSummary().players : [];
    },
    seasonLeader(kind) {
        return this.teamPlayerSummary()?.leaders?.[kind] || null;
    },
    playerSeasonStat(player, key) {
        return player?.seasonStats?.[key] ?? 0;
    },
    seasonWinRate() {
        const played = Number(this.teamStat('played') || 0);
        if (!played) return 0;
        return Math.round((Number(this.teamStat('won') || 0) / played) * 100);
    },
    averagePointsPerMatch() {
        const played = Number(this.teamStat('played') || 0);
        if (!played) return '0.00';
        return (Number(this.teamStat('points') || 0) / played).toFixed(2);
    },
    averageTonsPerAppearance() {
        const apps = Number(this.playerTotals('appearances') || 0);
        if (!apps) return '0.00';
        return (Number(this.playerTotals('tons') || 0) / apps).toFixed(2);
    },
    seasonChartRows() {
        const rows = this.teamStatsRows();
        const maxPoints = Math.max(1, ...rows.map((row) => Number(row?.points || 0)));
        return rows
            .slice()
            .sort((a, b) => Number(a?.season || 0) - Number(b?.season || 0))
            .map((row) => ({
                ...row,
                barHeight: Math.max(16, Math.round((Number(row?.points || 0) / maxPoints) * 100)),
                isSelected: String(row?.season || '') === String(this.selectedSeason || ''),
            }));
    },
    async selectSeason(season) {
        if (!season || String(season) === String(this.selectedSeason || '')) return;
        this.selectedSeason = String(season);
        await this.changeSeason();
    },
    resultFormText() {
        const won = Number(this.teamStat('won') || 0);
        const drawn = Number(this.teamStat('drawn') || 0);
        const lost = Number(this.teamStat('lost') || 0);
        return `${won}-${drawn}-${lost}`;
    },
    seasonLabel() {
        return this.team?.seasonUsed || this.selectedSeason || CURRENT_SEASON;
    },
    leaderName(entry) {
        if (!entry) return '???';
        return entry.nickname ? `${entry.fullName} (${entry.nickname})` : entry.fullName;
    },
    playerDisplayName(player) {
        if (!player) return 'Player';
        return player.nickname ? `${player.fullName} (${player.nickname})` : player.fullName;
    },
    playerNickname(player) {
        return String(player?.nickname || '').trim();
    },
    playerInitials(player) {
        const name = String(player?.fullName || '').trim();
        if (!name) return 'P';
        return name.split(/\s+/).slice(0, 2).map((part) => part[0]?.toUpperCase() || '').join('') || 'P';
    },
    teamCaptainPlayerId() {
        const c = this.team?.captainPlayerId;
        if (!c) return '';
        if (typeof c === 'object') return String(c._id || '');
        return String(c);
    },
    playerCardTone(player) {
        const cap = this.teamCaptainPlayerId();
        if (cap && String(player?._id || '') === cap) {
            return 'border-amber-300 bg-gradient-to-br from-amber-50 to-white dark:border-amber-800 dark:from-amber-950/30 dark:to-neutral-900';
        }
        if (String(player?.status || '') === 'reserve') {
            return 'border-slate-200 bg-gradient-to-br from-slate-50 to-white dark:border-neutral-800 dark:from-neutral-900 dark:to-neutral-900';
        }
        return 'border-sky-100 bg-gradient-to-br from-sky-50/80 to-white dark:border-sky-900/40 dark:from-sky-950/20 dark:to-neutral-900';
    },
    captainBadgeText(player) {
        const cap = this.teamCaptainPlayerId();
        if (cap && String(player?._id || '') === cap) return 'Captain';
        if (String(player?.status || '') === 'reserve') return 'Reserve';
        return 'Roster';
    },
    fixtureLegsFor(f) {
        if (!f) return null;
        if (f.homeScore != null && f.awayScore != null) {
            return this.fixtureIsHome(f) ? Number(f.homeScore) : Number(f.awayScore);
        }
        if (f.legsHome != null && f.legsAway != null) {
            return this.fixtureIsHome(f) ? Number(f.legsHome) : Number(f.legsAway);
        }
        if (f.legsFor != null) return Number(f.legsFor);
        return null;
    },
    fixtureLegsAgainst(f) {
        if (!f) return null;
        if (f.homeScore != null && f.awayScore != null) {
            return this.fixtureIsHome(f) ? Number(f.awayScore) : Number(f.homeScore);
        }
        if (f.legsHome != null && f.legsAway != null) {
            return this.fixtureIsHome(f) ? Number(f.legsAway) : Number(f.legsHome);
        }
        if (f.legsAgainst != null) return Number(f.legsAgainst);
        return null;
    },
    fixtureResultToken(f) {
        const legsFor = this.fixtureLegsFor(f);
        const legsAgainst = this.fixtureLegsAgainst(f);
        if (legsFor == null || legsAgainst == null) return '?';
        if (legsFor > legsAgainst) return 'W';
        if (legsFor < legsAgainst) return 'L';
        return 'D';
    },
    fixtureResultClasses(f) {
        const token = this.fixtureResultToken(f);
        if (token === 'W') return 'bg-emerald-500 text-white ring-2 ring-emerald-400/35';
        if (token === 'L') return 'bg-rose-500 text-white ring-2 ring-rose-400/35';
        return 'bg-amber-400 text-neutral-900 ring-2 ring-amber-300/40';
    },

    fixtureOutcome(f) {
        const t = this.fixtureResultToken(f);
        if (t === 'W') return 'win';
        if (t === 'L') return 'loss';
        if (t === 'D') return 'draw';
        return null;
    },

    recentFixtureRowClass(f) {
        const o = this.fixtureOutcome(f);
        if (o === 'win') return 'border-l-4 border-emerald-500 bg-emerald-500/10';
        if (o === 'loss') return 'border-l-4 border-rose-500 bg-rose-500/10';
        if (o === 'draw') return 'border-l-4 border-amber-400 bg-amber-500/10';
        return 'bg-white/5';
    },

    recentFixtureScoreClass(f) {
        const o = this.fixtureOutcome(f);
        if (o === 'win') return 'text-emerald-300';
        if (o === 'loss') return 'text-rose-300';
        if (o === 'draw') return 'text-amber-300';
        return 'text-white';
    },

    recentFixtureOutcomeLabelClass(f) {
        const o = this.fixtureOutcome(f);
        if (o === 'win') return 'text-emerald-400/90';
        if (o === 'loss') return 'text-rose-400/90';
        if (o === 'draw') return 'text-amber-400/90';
        return 'text-neutral-500';
    },

    recentFixtureOutcomeText(f) {
        const o = this.fixtureOutcome(f);
        if (o === 'win') return 'Win';
        if (o === 'loss') return 'Loss';
        if (o === 'draw') return 'Draw';
        return '';
    },

    /** Compact pill above date in Upcoming / Recent fixture rows */
    recentMatchOutcomePillClass(f) {
        const o = this.fixtureOutcome(f);
        if (o === 'win') {
            return 'border border-emerald-500/45 bg-emerald-500/20 text-emerald-100';
        }
        if (o === 'loss') {
            return 'border border-rose-500/45 bg-rose-500/20 text-rose-100';
        }
        if (o === 'draw') {
            return 'border border-amber-400/45 bg-amber-500/15 text-amber-100';
        }
        return 'border border-white/12 bg-white/[0.06] text-neutral-400';
    },

    recentMatchOutcomePillText(f) {
        return this.recentFixtureOutcomeText(f) || '—';
    },

    recentFormRows() {
        const rows = Array.isArray(this.results) ? this.results.slice().reverse() : [];
        const maxLegs = Math.max(1, ...rows.map((f) => Math.max(this.fixtureLegsFor(f) || 0, this.fixtureLegsAgainst(f) || 0)));
        return rows.map((fixture) => {
            const legsFor = this.fixtureLegsFor(fixture) || 0;
            const legsAgainst = this.fixtureLegsAgainst(fixture) || 0;
            return {
                fixture,
                opponent: this.fixtureOpponentName(fixture),
                token: this.fixtureResultToken(fixture),
                barFor: Math.max(10, Math.round((legsFor / maxLegs) * 100)),
                barAgainst: Math.max(10, Math.round((legsAgainst / maxLegs) * 100)),
                legsFor,
                legsAgainst,
            };
        });
    },
    captainName() {
        const c = this.team?.captainPlayerId;
        if (!c) return '—';
        return c.nickname ? `${c.fullName}` : c.fullName;
    },
    venueName() {
        const v = this.team?.venue;
        if (!v) return '-';
        return v.name;
    },
    venueUrl() {
        const v = this.team?.venue;
        if (!v) return null;
        return `/admin/venues/${v._id}`;
    },
    formatDate(iso) {
        if (!iso) return '—';
        try {
            return new Date(iso).toLocaleDateString('en-GB', {
                day: '2-digit', month: 'short', year: 'numeric', timeZone: 'Asia/Bangkok'
            });
        } catch { return '—'; }
    },
    formatDateTime(iso) {
        if (!iso) return '—';
        try {
            return new Date(iso).toLocaleString('en-GB', {
                day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
                timeZone: 'Asia/Bangkok'
            });
        } catch { return '—'; }
    },
    _normId(x) {
        if (!x) return null;
        return (typeof x === 'object') ? (x._id || x.id || null) : (typeof x === 'string' ? x : null);
    },
    fixtureIsHome(f) {
        const me = this._normId(this.id);
        return me && this._normId(f.homeTeamId) === me;
    },
    fixtureOpponentObj(f) {
        return this.fixtureIsHome(f) ? f.awayTeamId : f.homeTeamId;
    },
    fixtureOpponentName(f) {
        const opp = this.fixtureOpponentObj(f);
        return opp?.name || (this.fixtureIsHome(f) ? (f.awayTeamName || 'Away') : (f.homeTeamName || 'Home')) || '—';
    },
    fixtureOpponentLogo(f) {
        const opp = this.fixtureOpponentObj(f);
        return opp?.logoUrl || this.placeholderLogo;
    },
    homeAwayBadge(f) {
        return this.fixtureIsHome(f) ? 'H' : 'A';
    },
    venueLabel(f) {
        return f?.venue?.name ? `Venue: ${f.venue.name}` : '—';
    },
    fixtureScoreLine(f) {
        if (typeof f.score === 'string') return f.score;
        if (typeof f.result === 'string' && f.result.trim()) return `Score: ${f.result.trim()}`;
        if (f.homeScore != null && f.awayScore != null) {
            const isHome = (f.homeTeamId === this.id) || (f.homeTeam?._id === this.id);
            return isHome
                ? `Score: ${f.homeScore}–${f.awayScore}`
                : `Score: ${f.awayScore}–${f.homeScore}`;
        }
        if (f.legsHome != null && f.legsAway != null) {
            const base = this.fixtureIsHome(f)
                ? `Score: ${f.legsHome}–${f.legsAway}`
                : `Score: ${f.legsAway}–${f.legsHome}`;
            return f.forfeit ? `${base} (forfeit)` : base;
        }
        if (f.legsFor != null && f.legsAgainst != null) {
            return `Score: ${f.legsFor}–${f.legsAgainst}`;
        }
        return 'Score: N/A';
    },

    _findStandingsIndex(rows, team) {
        const id = team._id;
        const name = team.name;
        return rows.findIndex(r =>
            r.teamId === id ||
            r.team?._id === id ||
            r.teamName === name ||
            r.name === name
        );
    },

    _getIdFromUrl() {
        // expects /teams/:id
        const parts = (location.pathname || '').split('/').filter(Boolean);
        const idx = parts.indexOf('teams');
        return (idx >= 0 && parts[idx + 1]) ? parts[idx + 1] : null;
    },

}));
